<template>
    <BmvTable
        :columns="tableColumns"
        :data="tableData"
        :loading="loading"
    >
        <!-- ID -->
        <template #cell-id="{ row }">
            <span class="tf-id">#{{ row.id }}</span>
        </template>

        <!-- Name -->
        <template #cell-name="{ row }">
            <span class="tf-name">{{ row.name }}</span>
        </template>

        <!-- Type -->
        <template #cell-form_type_id="{ row }">
            <span class="tf-type-badge">
                <i class="fal fa-layer-group"></i>
                {{ getTypeName(row.form_type_id) }}
            </span>
        </template>

        <!-- Steps -->
        <template #cell-steps_count="{ row }">
            <span class="tf-num">{{ row.steps_count }}</span>
        </template>

        <!-- Attributes -->
        <template #cell-attributes_count="{ row }">
            <span class="tf-num">{{ row.attributes_count }}</span>
        </template>

        <!-- Tickets -->
        <template #cell-posts_count="{ row }">
            <router-link :to="`/posts?form_id=${row.id}`" class="tf-ticket-link">
                {{ row.posts_count }}
            </router-link>
        </template>

        <!-- Offline -->
        <template #cell-available_offline="{ row }">
            <span
                v-if="row.available_offline"
                class="tf-offline-badge tf-offline-badge--on"
                :title="$t('form_builder.offline.label')"
            >
                <i class="fal fa-wifi-slash"></i>
                {{ $t('common.yes') }}
            </span>
            <span
                v-else
                class="tf-offline-badge tf-offline-badge--off"
                :title="$t('form_builder.offline.label')"
            >
                <i class="fal fa-wifi"></i>
                {{ $t('common.no') }}
            </span>
        </template>

        <!-- Active -->
        <template #cell-active="{ row }">
            <label class="tf-toggle">
                <input
                    type="checkbox"
                    :checked="row.active"
                    @change="toggleActive(row)"
                />
                <span class="tf-toggle-slider"></span>
            </label>
        </template>

        <!-- Actions -->
        <template #cell-actions="{ row }">
            <div class="d-flex align-items-center justify-content-center" style="gap: 6px;">
                <router-link
                    :to="`/forms/${row.id}`"
                    class="tf-btn tf-btn--view"
                    :title="$t('forms.view')"
                >
                    <i class="fal fa-eye" style="color: #fff !important;"></i>
                </router-link>
                <router-link
                    :to="`/forms/${row.id}/edit`"
                    class="tf-btn tf-btn--edit"
                    :title="$t('forms.edit')"
                >
                    <i class="fal fa-pen" style="color: #fff !important;"></i>
                </router-link>
            </div>
        </template>
    </BmvTable>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions, mapState } from 'vuex'
import BmvTable from '@/components/UI/BmvTable'

export default {
    name: 'TabForms',
    components: { BmvTable },
    props: {
        forms: {
            type: Array,
            default: null
        },
        types: {
            type: Array,
            default: null
        }
    },
    data: function () {
        return {
            loading: false,
            tableColumns: [
                { field: 'id', label: 'ID', width: '60px' },
                { field: 'name', label: this.$t('forms.form') },
                { field: 'form_type_id', label: this.$t('form_types.form_type') },
                { field: 'steps_count', label: this.$t('forms.steps'), width: '90px', align: 'center' },
                { field: 'attributes_count', label: this.$t('forms.attributes'), width: '90px', align: 'center' },
                { field: 'posts_count', label: this.$t('forms.tickets'), width: '90px', align: 'center' },
                { field: 'available_offline', label: this.$t('form_builder.offline.short'), width: '110px', align: 'center' },
                { field: 'active', label: this.$t('forms.active'), width: '80px', align: 'center' },
                { field: 'actions', label: this.$t('forms.actions'), width: '110px', align: 'center' }
            ]
        }
    },
    created() {
        if (!this.forms) {
            this.getForms()
        }
    },
    computed: {
        ...mapState('form', {
            storeForms: (state) => state.forms,
            config: (state) => state.formsConfig
        }),
        tableData() {
            return this.forms || this.storeForms
        }
    },
    methods: {
        ...mapActions('form', ['getForms', 'toggleFormActive']),
        toggleActive(form) {
            this.toggleFormActive({ formId: form.id, active: !form.active })
                .then(() => {
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('success.title')
                    })
                })
                .catch((error) => {
                    this.$store.dispatch('dispatchError', error)
                })
        },
        getTypeName(typeId) {
            if (!typeId || !this.types) return '—'
            const type = this.types.find((t) => t.id === typeId)
            return type ? type.name : '—'
        }
    }
}
</script>

<style scoped>
.tf-id {
    font-size: 0.78rem;
    font-weight: 600;
    color: #94a3b8;
}

.tf-name {
    font-weight: 600;
    color: #0f172a;
}

.tf-type-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    background: #eff6ff;
    color: #3b82f6;
    border-radius: 20px;
    font-size: 0.78rem;
    font-weight: 600;
    white-space: nowrap;
}

.tf-type-badge i {
    font-size: 0.72rem;
}

.tf-num {
    font-weight: 600;
    color: #64748b;
}

/* Offline badge */
.tf-offline-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.78rem;
    font-weight: 600;
    white-space: nowrap;
}

.tf-offline-badge i {
    font-size: 0.72rem;
}

.tf-offline-badge--on {
    background: #ecfdf5;
    color: #059669;
}

.tf-offline-badge--off {
    background: #f1f5f9;
    color: #94a3b8;
}

.tf-ticket-link {
    font-weight: 700;
    color: #0891B2;
    text-decoration: none;
    transition: color 0.15s;
}

.tf-ticket-link:hover {
    color: #0e7490;
    text-decoration: underline;
}

.tf-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    text-decoration: none;
    transition: all 0.2s ease;
}

.tf-btn i {
    font-size: 0.78rem;
}

.tf-btn--view {
    background: #0891B2;
}

.tf-btn--view:hover {
    background: #0e7490;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(8, 145, 178, 0.3);
}

.tf-btn--edit {
    background: #f59e0b;
}

.tf-btn--edit:hover {
    background: #d97706;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(245, 158, 11, 0.3);
}

/* Toggle */
.tf-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin: 0;
}

.tf-toggle input {
    display: none;
}

.tf-toggle-slider {
    position: relative;
    width: 36px;
    height: 20px;
    background: #cbd5e1;
    border-radius: 20px;
    transition: all 0.2s ease;
}

.tf-toggle-slider::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    background: #fff;
    border-radius: 50%;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.tf-toggle input:checked + .tf-toggle-slider {
    background: #0891b2;
}

.tf-toggle input:checked + .tf-toggle-slider::after {
    transform: translateX(16px);
}

.tf-toggle:hover .tf-toggle-slider {
    background: #94a3b8;
}

.tf-toggle:hover input:checked + .tf-toggle-slider {
    background: #0e7490;
}
</style>
